import { Fragment, useCallback, useEffect, useMemo, useRef, type ReactNode } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import type { ApiFailure } from '../api.js';
import { Button, IconButton } from './Button.js';
import { SelectField } from './Field.js';
import { Icon } from './Icon.js';
import { Skeleton } from './Spinner.js';
import { DENIED_NEXT_STEP, FailureView, StateView } from './StateView.js';
import { Banner } from './Banner.js';
import { foldDisclosureLabel, foldNote, FOLD_DISCLOSURE_KEY } from './fold.js';
import { foldMemoryKey } from './fold-memory.js';
import { directionLabels, parseSort, sortRows, toggleSort, writeSort, type DirectionWords, type SortParamOptions, type SortState, type SortValue, type SortValueType } from './sort-params.js';
import { useIsMobile } from './use-media.js';
import { useColumnFold } from './use-fold.js';

// Tables (04-data §1): real <table> markup, every natural-order column sortable by a real button
// filling its header, aria-sort on the <th>, the default sort visible, sort persisted in the URL
// under a per-table namespace, sticky header, the four states as full-width rows, and below
// --bp-md either card-per-row with a visible sort control or horizontal scroll in the table's own
// container. From --bp-md up the box never scrolls sideways: a table too wide for the width its
// container measures folds its declared columns (§1.1, §1.7 *Folding*; jeeves ADR 0168) — the
// folded values reachable from each row's disclosure, their count and names stated beneath the
// table, a sort on a folded column kept and stated by the sort control there. Highlighting by id scrolls the row
// into view (06-modules §1.9, §5.5).

export interface DataTableColumn<Row> {
  /** The sort key the URL and the API share, e.g. `started_at`. Unique per table. */
  key: string;
  header: ReactNode;
  /** Plain-text name for the mobile sort control and card labels; required when `header` is not a string. */
  name?: string;
  cell: (row: Row) => ReactNode;
  /** Governs type-aware client sorting and the direction words ("oldest first"). Default text. */
  type?: SortValueType;
  /**
   * The column's own direction words where the type's misname its order — an enum of two kinds reads "yours first" /
   * "shared first", never "first to last" (04-data §1.2: the words name what the reader gets). Used by the mobile
   * sort control, the header's accessible name and the caption alike.
   */
  directionWords?: DirectionWords;
  /** Default true. Only actions, selection, prose and order-is-the-data columns may opt out (§1.2). */
  sortable?: boolean;
  /** The raw value for client-side sorting (`clientSort`); enums pass a rank number. */
  sortValue?: (row: Row) => SortValue;
  /** Numbers right-aligned with tabular numerals (§1.1). */
  numeric?: boolean;
  align?: 'start' | 'end';
  /** CSS width or min width hint for the column. */
  width?: string;
  /**
   * Card mode role (§1.4 A): `title` becomes the card heading (first column by default), `meta`
   * sits under the title, `field` is a label/value pair, `actions` goes to the card foot,
   * `hidden` is dropped — columns that exist only for scanning are dropped, not shrunk.
   */
  card?: 'title' | 'meta' | 'field' | 'actions' | 'hidden';
  /** Title attribute on the header, e.g. to spell out a unit. */
  headerTitle?: string;
  /**
   * The column's place in the table's declared folding order (04-data §1.7 *Folding*; ADR 0168): from --bp-md up, where
   * the columns do not fit the width the table's container measures, they fold in this order, 1 first — the columns
   * that exist only for scanning first, supplementary values next, the ones that make a row comparable last. Omitted:
   * the column never folds. The identifying column, selection, row actions and a module's central column carry none.
   */
  fold?: number;
  className?: string;
}

export type DataTableStatus =
  | { kind: 'ready' }
  | { kind: 'loading'; rowCount?: number }
  | { kind: 'error'; failure: ApiFailure; onRetry?: () => void }
  | { kind: 'denied'; description?: ReactNode };

export interface DataTableEmpty {
  title: ReactNode;
  description?: ReactNode;
  /** The action that would fill the table. */
  action?: ReactNode;
}

export interface DataTableFiltered {
  /** True when a filter or search is narrowing the rows. */
  active: boolean;
  onClear: () => void;
  title?: ReactNode;
  description?: ReactNode;
}

export interface DataTableSort<K extends string = string> {
  defaultSort: SortState<K>;
  /** Prefix for the `sort`/`dir` params; omit for the page's main table. */
  namespace?: string;
}

export interface DataTableProps<Row, K extends string = string> {
  /** Names the table for assistive technology and the caption; sentence case. */
  caption: string;
  /**
   * What the table holds, as its error and denied sentences name it — "the AI calls", "your Fellow syncs" — in running-text
   * case, product names keeping their capitals. Why never derived from the caption (05-content §1: "Product and feature
   * names keep their own capitalisation"; §8: no sentence built from fragments; the Phase 1c audit's R19 and D30): the
   * caption lowercased gave "We couldn't load fellow syncs, newest first" and "audit log. the timestamp column is …".
   * Every table in src/client passes one (test/server/client-helpers/stitched-sentences.test.ts); omitted, the sentences
   * say "this table".
   */
  failureNoun?: string;
  /** Show the caption visually (it is always present for AT). */
  captionVisible?: boolean;
  columns: readonly DataTableColumn<Row>[];
  rows: readonly Row[];
  rowKey: (row: Row) => string;
  status?: DataTableStatus;
  sort: DataTableSort<K>;
  /** Sort the given rows here (small, fully-loaded tables). Otherwise the caller sorts server-side. */
  clientSort?: boolean;
  empty: DataTableEmpty;
  filtered?: DataTableFiltered;
  /** A note that some cells could not be resolved (§1.3 partial); the table still renders. */
  partial?: ReactNode;
  /** Row id to highlight and scroll into view on arrival (`?highlight=`). */
  highlightId?: string | null;
  /** Called once when the rows are ready and the highlighted id is not among them. */
  onHighlightMissing?: (id: string) => void;
  /** Mobile strategy (§1.4). Cards are the default; scroll suits genuinely comparative data. */
  mobile?: 'cards' | 'scroll';
  rowClassName?: (row: Row) => string | undefined;
  /** DOM id per row, for anchors; defaults to `row-<key>`. */
  rowDomId?: (row: Row) => string;
  /**
   * A row's detail (§1.12): rendered as a second row of the same table beneath the row, spanning the
   * columns from `detailFrom` on, and on a card under the title. Return null for a row with none.
   */
  rowDetail?: (row: Row) => ReactNode;
  /** The column index the detail row starts under (the columns before it are left blank). Default 0. */
  detailFrom?: number;
  /** Sticky header while the table can exceed a viewport (§1.1). Default true. */
  stickyHeader?: boolean;
  /** Rendered under the table: counts, load-more. */
  footer?: ReactNode;
  /**
   * The words naming a row, where its identifying cell is not plain text (a link, say) and its sort value is not the name a
   * person reads — a date's instant: the fold disclosure is named by them (04-data §1.12: "an accessible name naming its
   * row"). Omitted, the identifying cell's text or its sort value names the row, as before.
   */
  rowLabel?: (row: Row) => string;
  className?: string;
}

/** Sort state in the URL for a table; the page uses the same hook to build its API query. */
export function useUrlSort<K extends string>(options: SortParamOptions<K>): [SortState<K>, (next: SortState<K>) => void] {
  const [params, setParams] = useSearchParams();
  // Why: callers pass fresh option objects each render; keying on the content keeps the setter stable.
  const signature = `${options.namespace ?? ''}|${options.keys.join(',')}|${options.defaultSort.key}|${options.defaultSort.dir}`;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const stable = useMemo(() => options, [signature]);
  const sort = useMemo(() => parseSort(params, stable), [params, stable]);
  const setSort = useCallback((next: SortState<K>) => {
    setParams((current) => writeSort(current, next, stable), { replace: true });
  }, [setParams, stable]);
  return [sort, setSort];
}

/** Sort options for a table's columns, memoised by the caller's column keys. */
export function sortOptionsFor<Row, K extends string>(columns: readonly DataTableColumn<Row>[], sort: DataTableSort<K>): SortParamOptions<K> {
  return {
    namespace: sort.namespace,
    keys: columns.filter((column) => column.sortable !== false).map((column) => column.key as K),
    defaultSort: sort.defaultSort,
  };
}

function columnName<Row>(column: DataTableColumn<Row>): string {
  if (column.name) return column.name;
  return typeof column.header === 'string' ? column.header : column.key;
}

/**
 * The words naming a row for its fold disclosure (04-data §1.12: an accessible name naming its row): the identifying
 * column's shown text where it is text, else its sort value where that is text — never a position, which a sort moves.
 */
function rowLabelOf<Row>(identity: DataTableColumn<Row> | undefined, row: Row): string {
  if (!identity) return 'this row';
  const shown = identity.cell(row);
  if (typeof shown === 'string' || typeof shown === 'number') return String(shown);
  const value = identity.sortValue?.(row);
  if (typeof value === 'string' && value.trim() !== '') return value;
  return columnName(identity);
}

/**
 * Where the disclosure column stands: before the trailing row actions (§1.1, actions right), else last. Its cell is its
 * own (§1.12: the trigger is a real button in a cell of its own).
 */
function disclosureIndexOf<Row>(columns: readonly DataTableColumn<Row>[]): number {
  let index = columns.length;
  while (index > 0 && columns[index - 1]?.card === 'actions') index -= 1;
  return index;
}

export function DataTable<Row, K extends string = string>({
  caption, failureNoun = 'this table', captionVisible = false, columns, rows, rowKey, status = { kind: 'ready' }, sort, clientSort = false, empty, filtered, partial,
  highlightId, onHighlightMissing, mobile = 'cards', rowClassName, rowDomId, rowDetail, detailFrom = 0, stickyHeader = true, footer, rowLabel, className,
}: DataTableProps<Row, K>) {
  const isMobile = useIsMobile();
  const sortOptions = useMemo(() => sortOptionsFor(columns, sort), [columns, sort]);
  const [sortState, setSortState] = useUrlSort<K>(sortOptions);
  const sortedColumn = columns.find((column) => column.key === sortState.key);

  const orderedRows = useMemo(() => {
    if (!clientSort || !sortedColumn) return rows;
    const valueOf = sortedColumn.sortValue ?? ((row: Row) => {
      const rendered = sortedColumn.cell(row);
      return typeof rendered === 'string' || typeof rendered === 'number' ? rendered : null;
    });
    return sortRows(rows, valueOf, sortedColumn.type ?? 'text', sortState.dir);
  }, [rows, clientSort, sortedColumn, sortState.dir]);

  const ready = status.kind === 'ready';
  const cards = isMobile && mobile === 'cards';
  // Folding is the desktop table's (§1.1): below --bp-md the card form, or strategy B's own scrolling box, stands.
  const foldable = columns.some((column) => column.fold !== undefined);
  // Why the page's path and the table's own name key the opened rows (fold-memory.ts; the owner's ruling Q19, "Remember opened folds"): two tables on one page, or one table on two records, are different lists.
  const { pathname } = useLocation();
  const fold = useColumnFold(!isMobile, columns, { rows: orderedRows, status: status.kind }, foldMemoryKey(pathname, sort.namespace ?? caption));
  const foldedColumns = columns.filter((column) => fold.folded.has(column.key));
  const folding = !cards && foldedColumns.length > 0;
  const isShown = (column: DataTableColumn<Row>): boolean => !folding || !fold.folded.has(column.key);
  const shownCount = columns.filter(isShown).length + (folding ? 1 : 0);

  // Highlight: scroll the row into view once per id, and say so when it is not in the view.
  // Why two refs: an id reported missing is not done with — the page may widen a filter in answer,
  // and when the rows then include it the row must still be scrolled to; only the scroll is once.
  const scrolled = useRef<string | null>(null);
  const reportedMissing = useRef<string | null>(null);
  const container = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!highlightId || !ready || scrolled.current === highlightId) return;
    const present = orderedRows.some((row) => rowKey(row) === highlightId);
    if (!present) {
      if (reportedMissing.current !== highlightId) {
        reportedMissing.current = highlightId;
        onHighlightMissing?.(highlightId);
      }
      return;
    }
    // Why the id is marked done inside the frame, not when the frame is booked: this effect runs
    // again on any re-render (a caller's inline rowKey, or a sibling request landing — the record
    // page's transcript arrives a few ms after its to-dos), and its cleanup cancels the booked frame.
    // Marked at booking, a cancelled scroll counted as done and the deep-linked row stayed
    // highlighted but off-screen; marked here, the next run books the scroll again.
    const frame = requestAnimationFrame(() => {
      const element = container.current?.querySelector<HTMLElement>(`[data-row-id="${CSS.escape(highlightId)}"]`);
      if (!element) return;
      element.scrollIntoView({ block: 'center', behavior: 'auto' });
      scrolled.current = highlightId;
    });
    return () => cancelAnimationFrame(frame);
  }, [highlightId, ready, orderedRows, rowKey, onHighlightMissing]);

  const sortLabel = (column: DataTableColumn<Row>): string => {
    const name = columnName(column);
    const words = directionLabels(column.type, column.directionWords);
    if (column.key === sortState.key) return `${name}, sorted ${sortState.dir === 'asc' ? words.asc : words.desc}. Activate to sort ${sortState.dir === 'asc' ? words.desc : words.asc}`;
    return `${name}. Activate to sort ${words.asc}`;
  };

  const fullWidth = (content: ReactNode): ReactNode => (
    <tr className="data-table__state-row">
      <td colSpan={shownCount}>{content}</td>
    </tr>
  );

  const stateContent = (): ReactNode => {
    switch (status.kind) {
      case 'error':
        return <FailureView failure={status.failure} what={failureNoun} onRetry={status.onRetry} compact />;
      case 'denied':
        // Why the next step too: a denied state says how to request access (03-components §1), as FailureView's does.
        return <StateView kind="denied" description={status.description ?? `You do not have access to ${failureNoun}. ${DENIED_NEXT_STEP}`} compact />;
      case 'loading':
        return null;
      case 'ready':
      default:
        if (orderedRows.length > 0) return null;
        if (filtered?.active) {
          return (
            <StateView
              kind="empty"
              icon="filter"
              title={filtered.title ?? 'Nothing matches these filters'}
              description={filtered.description ?? 'Try widening the search, or clear the filters to see everything.'}
              action={<Button variant="secondary" onClick={filtered.onClear}>Clear filters</Button>}
              compact
            />
          );
        }
        return <StateView kind="empty" title={empty.title} description={empty.description} action={empty.action} compact />;
    }
  };

  const skeletonRows = status.kind === 'loading' ? Math.max(1, Math.min(status.rowCount ?? 5, 12)) : 0;

  // The sort control: above the cards (§1.4), and in a folded table's fold bar whenever a sortable column is folded, so
  // that a folded column stays sortable and a sort on it stays stated (§1.7 *Folding*: "sorting survives folding").
  const sortControlShown = cards || (folding && foldedColumns.some((column) => column.sortable !== false));
  const sortControl = sortControlShown ? (
    <div className="data-table__sort-control">
      <SelectField
        label="Sort by"
        value={`${sortState.key}:${sortState.dir}`}
        onChange={(event) => {
          const [key, dir] = event.target.value.split(':') as [K, 'asc' | 'desc'];
          setSortState({ key, dir });
        }}
        options={columns.filter((column) => column.sortable !== false).flatMap((column) => {
          const words = directionLabels(column.type, column.directionWords);
          return [
            { value: `${column.key}:asc`, label: `${columnName(column)}, ${words.asc}` },
            { value: `${column.key}:desc`, label: `${columnName(column)}, ${words.desc}` },
          ];
        })}
      />
    </div>
  ) : null;

  // Why `is-overflowing`: from --bp-md up the box never scrolls (components.css) — save when even the whole folding set
  // leaves the table wider than its box, the last resort that keeps every value in reach rather than clipped.
  const classes = ['data-table-wrap', cards ? 'is-cards' : '', mobile === 'scroll' ? 'is-scroll' : '', folding ? 'is-folding' : '', !cards && fold.overflowing ? 'is-overflowing' : '', className ?? ''].filter(Boolean).join(' ');

  if (cards) {
    const titleColumn = columns.find((column) => column.card === 'title') ?? columns[0];
    const metaColumns = columns.filter((column) => column.card === 'meta');
    const actionColumns = columns.filter((column) => column.card === 'actions');
    const fieldColumns = columns.filter((column) => column !== titleColumn && !metaColumns.includes(column) && !actionColumns.includes(column) && column.card !== 'hidden');
    const state = stateContent();
    return (
      <div className={classes} ref={container}>
        {partial ? <Banner tone="warning">{partial}</Banner> : null}
        {sortControl}
        {captionVisible ? <p className="data-table__caption">{caption}</p> : null}
        {state ? <div className="data-cards__state">{state}</div> : null}
        {/* Why role="list" on both lists (01-foundations §2; Phase 1c finding D24): the list reset in base.css reaches
            `ol[role='list']` alone, so without it each phone card list kept the browser's own 40-pixel inline padding — a
            value off the spacing ramp, every card indented from the page's gutter. The role also keeps the list a list
            to Safari's screen reader, which drops it from an unstyled-marker list. */}
        {status.kind === 'loading' ? (
          <ol className="data-cards" role="list" aria-label={caption} aria-busy="true">
            {Array.from({ length: skeletonRows }, (_, index) => (
              <li key={index} className="data-card data-card--skeleton" aria-hidden="true">
                <Skeleton width="60%" />
                <Skeleton width="40%" />
                <Skeleton width="80%" />
              </li>
            ))}
          </ol>
        ) : null}
        {ready && orderedRows.length > 0 ? (
          <ol className="data-cards" role="list" aria-label={caption}>
            {orderedRows.map((row) => {
              const key = rowKey(row);
              const highlighted = highlightId === key;
              const detail = rowDetail?.(row) ?? null;
              return (
                <li
                  key={key}
                  id={rowDomId ? rowDomId(row) : `row-${key}`}
                  data-row-id={key}
                  className={['data-card', highlighted ? 'is-highlighted' : '', rowClassName?.(row) ?? ''].filter(Boolean).join(' ')}
                  aria-current={highlighted ? 'true' : undefined}
                >
                  {titleColumn ? <div className="data-card__title">{titleColumn.cell(row)}</div> : null}
                  {detail ? <div className="data-card__detail">{detail}</div> : null}
                  {metaColumns.length > 0 ? (
                    <div className="data-card__meta cluster">
                      {metaColumns.map((column) => <span key={column.key}>{column.cell(row)}</span>)}
                    </div>
                  ) : null}
                  {fieldColumns.length > 0 ? (
                    <dl className="data-card__fields">
                      {fieldColumns.map((column) => (
                        <div key={column.key} className="data-card__field">
                          <dt>{columnName(column)}</dt>
                          <dd className={column.numeric ? 'num' : undefined}>{column.cell(row)}</dd>
                        </div>
                      ))}
                    </dl>
                  ) : null}
                  {actionColumns.length > 0 ? (
                    <div className="data-card__actions cluster">
                      {actionColumns.map((column) => <span key={column.key}>{column.cell(row)}</span>)}
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ol>
        ) : null}
        {footer ? <div className="data-table__footer">{footer}</div> : null}
      </div>
    );
  }

  const state = stateContent();
  const identity = columns.find((column) => column.card === 'title') ?? columns[0];
  const disclosureAt = disclosureIndexOf(columns);
  const lead = Math.min(Math.max(detailFrom, 0), columns.length - 1);
  // Why the lead counts shown columns: a detail or fold row starts under the column it is meant to, whatever folded before it.
  const shownLead = columns.slice(0, lead).filter(isShown).length;

  // The disclosure column: measured with the others (use-fold.ts), shown only while something is folded. Why every row of
  // a foldable table carries its cell, empty and folded away while nothing folds: the measuring pass shows every column,
  // and a row one cell short would slide its last cells under the wrong headers and measure them there.
  const idleDisclosureCell = foldable && !folding ? <td key={FOLD_DISCLOSURE_KEY} className="data-table__fold-cell is-folded" /> : null;
  const disclosureHeader = foldable ? (
    <th key={FOLD_DISCLOSURE_KEY} scope="col" data-column={FOLD_DISCLOSURE_KEY} className={['data-table__th', 'data-table__fold-th', folding ? '' : 'is-folded'].filter(Boolean).join(' ')}>
      <span className="visually-hidden">Folded columns</span>
      {/* Why a sizer with the button's own classes: at the min-content read a table cell's declared width does not count,
          so the header carries the button's width itself — on either pointer — and the fold counts the column it adds. */}
      <span className="btn btn--icon btn--sm data-table__fold-sizer" aria-hidden="true" />
    </th>
  ) : null;

  return (
    <div className={classes} ref={container}>
      {partial ? <Banner tone="warning">{partial}</Banner> : null}
      <div className="data-table__scroller scroll-x" ref={fold.scrollerRef}>
        <table ref={fold.tableRef} className={['data-table', stickyHeader ? 'data-table--sticky' : ''].filter(Boolean).join(' ')} aria-busy={status.kind === 'loading' || undefined}>
          <caption className={captionVisible ? 'data-table__caption' : 'visually-hidden'}>
            {caption}
            {sortedColumn ? <span className="visually-hidden">{`. Sorted by ${columnName(sortedColumn)}, ${sortState.dir === 'asc' ? directionLabels(sortedColumn.type, sortedColumn.directionWords).asc : directionLabels(sortedColumn.type, sortedColumn.directionWords).desc}.`}</span> : null}
          </caption>
          <thead>
            <tr>
              {columns.map((column, index) => {
                const sortable = column.sortable !== false;
                const active = column.key === sortState.key;
                const align = column.align ?? (column.numeric ? 'end' : 'start');
                return (
                  <Fragment key={column.key}>
                    {index === disclosureAt ? disclosureHeader : null}
                    <th
                      scope="col"
                      data-column={column.key}
                      aria-sort={sortable ? (active ? (sortState.dir === 'asc' ? 'ascending' : 'descending') : 'none') : undefined}
                      className={['data-table__th', `is-align-${align}`, active ? 'is-sorted' : '', isShown(column) ? '' : 'is-folded', column.className ?? ''].filter(Boolean).join(' ')}
                      style={column.width ? { inlineSize: column.width } : undefined}
                      title={column.headerTitle}
                    >
                      {sortable ? (
                        <button type="button" className="data-table__sort" onClick={() => setSortState(toggleSort(sortState, column.key as K, column.type))} aria-label={sortLabel(column)}>
                          <span className="data-table__header-text">{column.header}</span>
                          <span className="data-table__sort-icon" aria-hidden="true">
                            {active ? <Icon name={sortState.dir === 'asc' ? 'arrow-up' : 'arrow-down'} size={14} /> : <Icon name="sort" size={14} className="data-table__sort-idle" />}
                          </span>
                        </button>
                      ) : (
                        <span className="data-table__header-text">{column.header}</span>
                      )}
                    </th>
                  </Fragment>
                );
              })}
              {disclosureAt === columns.length ? disclosureHeader : null}
            </tr>
          </thead>
          <tbody>
            {status.kind === 'loading'
              ? Array.from({ length: skeletonRows }, (_, index) => (
                <tr key={index} className="data-table__skeleton-row" aria-hidden="true">
                  {columns.map((column, columnIndex) => (
                    <Fragment key={column.key}>
                      {columnIndex === disclosureAt ? (idleDisclosureCell ?? (folding ? <td className="data-table__fold-cell" /> : null)) : null}
                      <td data-column={column.key} className={[column.numeric ? 'is-align-end' : '', isShown(column) ? '' : 'is-folded'].filter(Boolean).join(' ') || undefined}>
                        <Skeleton width={columnIndex === 0 ? '70%' : '45%'} />
                      </td>
                    </Fragment>
                  ))}
                  {disclosureAt === columns.length ? (idleDisclosureCell ?? (folding ? <td className="data-table__fold-cell" /> : null)) : null}
                </tr>
              ))
              : null}
            {state ? fullWidth(state) : null}
            {ready && orderedRows.map((row) => {
              const key = rowKey(row);
              const highlighted = highlightId === key;
              const rowClasses = [highlighted ? 'is-highlighted' : '', rowClassName?.(row) ?? ''].filter(Boolean);
              const detail = rowDetail?.(row) ?? null;
              const domId = rowDomId ? rowDomId(row) : `row-${key}`;
              const foldId = `${domId}-folded`;
              const open = folding && fold.expanded.has(key);
              // The row's own disclosure (§1.12): a real button in a cell of its own, naming its row; its state is
              // aria-expanded, and the folded values follow it in document order, in a row of the same table.
              const disclosureCell = folding ? (
                <td key={FOLD_DISCLOSURE_KEY} className="data-table__fold-cell">
                  <IconButton
                    icon="chevron-down"
                    size="sm"
                    className="data-table__disclosure"
                    label={foldDisclosureLabel(rowLabel ? rowLabel(row) : rowLabelOf(identity, row))}
                    aria-expanded={open}
                    aria-controls={foldId}
                    onClick={() => fold.toggle(key)}
                  />
                </td>
              ) : idleDisclosureCell;
              return (
                <Fragment key={key}>
                  <tr
                    id={domId}
                    data-row-id={key}
                    className={rowClasses.join(' ') || undefined}
                    aria-current={highlighted ? 'true' : undefined}
                  >
                    {columns.map((column, index) => {
                      const align = column.align ?? (column.numeric ? 'end' : 'start');
                      return (
                        <Fragment key={column.key}>
                          {index === disclosureAt ? disclosureCell : null}
                          <td data-column={column.key} className={[`is-align-${align}`, column.numeric ? 'num' : '', isShown(column) ? '' : 'is-folded', column.className ?? ''].filter(Boolean).join(' ')}>
                            {column.cell(row)}
                          </td>
                        </Fragment>
                      );
                    })}
                    {disclosureAt === columns.length ? disclosureCell : null}
                  </tr>
                  {open ? (
                    <tr id={foldId} data-fold-of={key} className={['data-table__fold-row', ...rowClasses].join(' ')}>
                      {shownLead > 0 ? <td colSpan={shownLead} className="data-table__detail-lead" /> : null}
                      <td colSpan={shownCount - shownLead}>
                        <dl className="data-table__folded">
                          {foldedColumns.map((column) => (
                            <div key={column.key} className="data-table__folded-item" data-column={column.key}>
                              <dt>{columnName(column)}</dt>
                              <dd className={column.numeric ? 'num' : undefined}>{column.cell(row)}</dd>
                            </div>
                          ))}
                        </dl>
                      </td>
                    </tr>
                  ) : null}
                  {detail ? (
                    // Why a row and not a cell: the detail follows every cell of its row in document order,
                    // so the keyboard meets it after the row's controls, where the eye does (§1.12).
                    <tr data-row-detail={key} className={['data-table__detail-row', ...rowClasses].join(' ')}>
                      {shownLead > 0 ? <td colSpan={shownLead} className="data-table__detail-lead" /> : null}
                      <td colSpan={shownCount - shownLead}>{detail}</td>
                    </tr>
                  ) : null}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      {folding ? (
        // Why beneath the table and not above it: the fold is decided once the rows are laid out — the skeleton's cells
        // hold no content to measure, so a table that fits on its headers folds only when its rows land — and a bar that
        // appeared above the table then would push the header and every row down at the moment they arrive, the jump
        // ADR 0168 names a defect under the motion rules. Beneath the rows, its arrival moves nothing above it.
        // Why no live region: the fold follows the width, and a reader resizing a window is not told so at every step —
        // the count and names are here to be read, and announce nothing (ADR 0168; the brief's "nothing disruptive").
        <div className="data-table__fold-bar">
          <p className="data-table__fold-note" data-folded-count={foldedColumns.length}>{foldNote(foldedColumns.map(columnName))}</p>
          {sortControl}
        </div>
      ) : null}
      {footer ? <div className="data-table__footer">{footer}</div> : null}
    </div>
  );
}
